import { print } from 'graphql';

import createManyEventMutation from '~/shared/graphql-queries/events/create-many-event-mutation';
import type { BuilderType } from '~/types';

type CalendarEvent = Readonly<{
  id: string;
  title: string;
  start: string;
}>;

type CalendarEventInput = Readonly<{
  id?: string;
  title: string;
  start: string;
}>;

type RequestEventDeleteArgs = Readonly<{
  eventId: string;
}>;

function excludeById(array: CalendarEvent[], id: string): CalendarEvent[] {
  return array.filter((item) => item.id !== id);
}

function getTodayStr(): string {
  return new Date().toISOString().replace(/T.*$/, '');
}

const todayStr = getTodayStr();
let eventGuid = 0;

function createEventId(): string {
  return String(eventGuid++);
}

let eventDb: CalendarEvent[] = [
  {
    id: createEventId(),
    title: 'All-day event',
    start: todayStr,
  },
  {
    id: createEventId(),
    title: 'Timed event',
    start: `${todayStr}T12:00:00`,
  },
];

const DELAY = 200;
const simulateErrors = false;

const requestEventCreate = (plainEventObject: CalendarEventInput): Promise<string> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (simulateErrors) {
        reject(new Error('error'));
      } else {
        const newEventId = createEventId();

        const objWithId: CalendarEvent = {
          id: newEventId,
          title: plainEventObject.title,
          start: plainEventObject.start,
        };

        eventDb.push(objWithId);
        resolve(newEventId);
      }
    }, DELAY);
  });
};

const requestEventUpdate = (plainEventObject: CalendarEvent): Promise<CalendarEvent[]> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (simulateErrors) {
        reject(new Error('problem'));
      } else {
        eventDb = excludeById(eventDb, plainEventObject.id);
        eventDb.push(plainEventObject);
        resolve(eventDb);
      }
    }, DELAY);
  });
};

const requestEventDelete = ({ eventId }: RequestEventDeleteArgs): Promise<CalendarEvent[]> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (simulateErrors) {
        reject(new Error('problem'));
      } else {
        eventDb = excludeById(eventDb, eventId);
        resolve(eventDb);
      }
    }, DELAY);
  });
};

type QueryFnError = Readonly<{
  status: 'CUSTOM_ERROR';
  error: string;
}>;

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Unknown error';
}

export const calendarTagTypes = ['calendar'] as const;

export type CreateManyEventPayloadType = {
  courseId: string;
  userId: string | null;
  selectedCourseCategoryId: string | null;
  records: Array<{
    course: string;
    student?: string | null;
    teacher?: string | null;
    studentsGroup?: string | null;
    lesson: string;
    courseCategory: string;
    title?: string | null;
    color?: string | null;
    dateTime: string[];
    status?: string | null;
    eventType?: string | null;
  }>;
};

export const getCalendarQuery = (builder: BuilderType) => {
  return {
    createManyEvent: builder.mutation<unknown, CreateManyEventPayloadType>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createManyEventMutation),
          variables: {
            records: args.records,
          },
        }),
      }),
    }),
    requestEventDelete: builder.mutation<CalendarEvent[], RequestEventDeleteArgs>({
      async queryFn(args): Promise<{ data: CalendarEvent[] } | { error: QueryFnError }> {
        try {
          const data = await requestEventDelete(args);
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: getErrorMessage(error),
            },
          };
        }
      },
    }),

    requestEventUpdate: builder.mutation<CalendarEvent[], CalendarEvent>({
      async queryFn(args): Promise<{ data: CalendarEvent[] } | { error: QueryFnError }> {
        try {
          const data = await requestEventUpdate(args);
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: getErrorMessage(error),
            },
          };
        }
      },
    }),

    requestEventCreate: builder.mutation<string, CalendarEventInput>({
      async queryFn(args): Promise<{ data: string } | { error: QueryFnError }> {
        try {
          const data = await requestEventCreate(args);
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: getErrorMessage(error),
            },
          };
        }
      },
    }),
  };
};

export { createEventId, eventDb, requestEventCreate, requestEventDelete, requestEventUpdate };
